import {useRef, useState} from 'react';
import {gql, useQuery} from '@apollo/client';
import {Alert, Button, Empty, Pagination, Skeleton} from 'antd';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DownOutlined,
  HistoryOutlined,
  ReloadOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import styles from './inventoryActivity.module.scss';

export const INVENTORY_ACTIVITY = gql`
  query InventoryActivity($instrumentId: ID!, $page: Int!, $limit: Int!) {
    getInstrumentInventoryActivity(
      instrumentId: $instrumentId
      page: $page
      limit: $limit
    ) {
      total
      page
      limit
      data {
        id
        action
        actorType
        actorId
        createdAt
        details
        actor {
          id
          firstName
          lastName
        }
      }
    }
  }
`;

const PAGE_SIZE = 20;
const numberFormat = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 20,
});
const actionPresentation = {
  BROKER_TOP_UP: {
    label: 'Broker top-up',
    icon: ArrowDownOutlined,
    tone: 'added',
  },
  UNITS_ADDED: {label: 'Units added', icon: ArrowDownOutlined, tone: 'added'},
  UNITS_REMOVED: {
    label: 'Units removed',
    icon: ArrowUpOutlined,
    tone: 'removed',
  },
  SETTINGS_UPDATED: {
    label: 'Settings updated',
    icon: SettingOutlined,
    tone: 'settings',
  },
};
const fieldLabels = {
  lowUnitThreshold: 'Low unit threshold',
  autoBuyThresholdAmount: 'Auto-buy threshold',
  enabledForTrading: 'Enabled for trading',
};
const title = value =>
  String(value || '')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/^./, letter => letter.toUpperCase());
const fieldLabel = key => fieldLabels[key] || title(key);
const valueText = value => {
  if (value == null) {
    return 'Not recorded';
  }
  if (typeof value === 'boolean') {
    return value ? 'Enabled' : 'Disabled';
  }
  if (typeof value === 'number') {
    return numberFormat.format(value);
  }
  return typeof value === 'object' ? JSON.stringify(value) : String(value);
};

// Audit details may come back as a JSON scalar or a serialized JSON string.
// Invalid historical details must not prevent the rest of the history loading.
function parseDetails(raw) {
  try {
    const details = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return details && typeof details === 'object' && !Array.isArray(details)
      ? details
      : {};
  } catch {
    return {};
  }
}

function changesFor(details) {
  return Object.keys(details.to || {}).map(key => ({
    key,
    label: fieldLabel(key),
    before: details.from?.[key],
    after: details.to[key],
  }));
}

function transactionDate(value) {
  const date = moment.utc(value);
  return date.isValid() ? date.format('DD MMM YYYY') : 'Not recorded';
}

function movementDetails(details, currency) {
  const money = value => `${valueText(value)}${currency ? ` ${currency}` : ''}`;
  const entries = [
    ['Units', details.units, valueText],
    ['Price per unit', details.pricePerUnit, money],
    ['Broker', details.brokerName || details.brokerId, valueText],
    ['Actual broker fee', details.brokerFees, money],
  ]
    .filter(
      ([, value]) => value !== undefined && value !== null && value !== '',
    )
    .map(([label, value, format]) => [label, format(value)]);

  if (details.effectiveAt) {
    entries.push(['Transaction date', transactionDate(details.effectiveAt)]);
  }
  if (details.openedPosition) {
    entries.push(['Inventory position', 'Opened by this movement']);
  }
  return entries;
}

/** Plain-text detail projection retained for callers and audit checks. */
export function inventoryActivityDetails(raw) {
  const details = parseDetails(raw);
  const changes = changesFor(details).map(change => [
    change.label,
    `${valueText(change.before)} → ${valueText(change.after)}`,
  ]);
  const movement = movementDetails(details);
  if (details.currency) {
    movement.push(['Currency', details.currency]);
  }
  if (details.description) {
    movement.push(['Description', details.description]);
  }
  return [...changes, ...movement];
}

function eventSummary(event, details, currency) {
  const changes = changesFor(details);
  if (event.action === 'SETTINGS_UPDATED') {
    return changes
      .filter(change => change.before !== change.after)
      .map(change => (
        <span key={change.key}>
          {change.label}:{' '}
          <strong>
            {valueText(change.before)} → {valueText(change.after)}
          </strong>
          {change.key === 'autoBuyThresholdAmount' && currency
            ? ` ${currency}`
            : ''}
        </span>
      ));
  }

  const summary = [];
  if (details.units != null) {
    const direction =
      event.action === 'UNITS_REMOVED' || details.direction === 'OUTBOUND'
        ? 'removed'
        : ['BROKER_TOP_UP', 'UNITS_ADDED'].includes(event.action) ||
          details.direction === 'INBOUND'
        ? 'added'
        : '';
    summary.push(
      <span key="units">
        <strong>
          {valueText(details.units)} {details.units === 1 ? 'unit' : 'units'}
        </strong>{' '}
        {direction}
      </span>,
    );
  }
  if (details.brokerName) {
    summary.push(<span key="broker">{details.brokerName}</span>);
  }
  if (details.brokerFees != null) {
    summary.push(
      <span key="fee">
        Fee{' '}
        <strong>
          {valueText(details.brokerFees)}
          {currency ? ` ${currency}` : ''}
        </strong>
      </span>,
    );
  }
  const balance = changes.find(change => change.key === 'availableUnits');
  if (balance) {
    summary.push(
      <span key="balance">
        Balance{' '}
        <strong>
          {valueText(balance.before)} → {valueText(balance.after)}
        </strong>
      </span>,
    );
  }
  return summary;
}

function ActivityEvent({event, currency, expanded, onToggle}) {
  const details = parseDetails(event.details);
  const eventCurrency = details.currency || currency;
  const presentation = actionPresentation[event.action] || {
    label: title(event.action) || 'Inventory updated',
    icon: HistoryOutlined,
    tone: 'settings',
  };
  const Icon = presentation.icon;
  const actor =
    [event.actor?.firstName, event.actor?.lastName].filter(Boolean).join(' ') ||
    `Admin ${event.actorId || 'not recorded'}`;
  const date = event.createdAt ? moment.utc(event.createdAt) : moment.invalid();
  const recordedTime = date.isValid()
    ? date.format('HH:mm')
    : 'Time unavailable';
  const regionId = `inventory-event-${event.id}`;
  const changes = changesFor(details);
  const metadata = movementDetails(details, eventCurrency);
  const summary = eventSummary(event, details, eventCurrency);

  return (
    <li className={styles.event}>
      <button
        type="button"
        className={styles.eventToggle}
        aria-expanded={expanded}
        aria-controls={regionId}
        aria-label={`${presentation.label} by ${actor}, ${recordedTime} UTC`}
        onClick={onToggle}>
        <span
          className={`${styles.eventIcon} ${styles[presentation.tone]}`}
          aria-hidden="true">
          <Icon />
        </span>
        <span className={styles.eventContent}>
          <span className={styles.eventTitle}>{presentation.label}</span>
          <span className={styles.actor}>By {actor}</span>
          {summary.length > 0 && (
            <span className={styles.summary}>{summary}</span>
          )}
        </span>
        <span className={styles.eventAside}>
          <time dateTime={event.createdAt}>{recordedTime}</time>
          <span className={styles.disclosure}>
            {expanded ? 'Hide details' : 'View details'}
            <DownOutlined
              className={expanded ? styles.chevronOpen : ''}
              aria-hidden="true"
            />
          </span>
        </span>
      </button>
      <div id={regionId} hidden={!expanded} className={styles.eventDetails}>
        {changes.length > 0 && (
          <table className={styles.changes}>
            <caption className={styles.srOnly}>
              Recorded values before and after this action
            </caption>
            <thead>
              <tr>
                <th scope="col">Field</th>
                <th scope="col">Before</th>
                <th scope="col">After</th>
              </tr>
            </thead>
            <tbody>
              {changes.map(change => (
                <tr key={change.key}>
                  <th scope="row">{change.label}</th>
                  <td>{valueText(change.before)}</td>
                  <td>{valueText(change.after)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {metadata.length > 0 && (
          <dl className={styles.metadata}>
            {metadata.map(([label, value]) => (
              <div key={label}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
        )}
        {details.description && (
          <div className={styles.note}>
            <h4>Note</h4>
            <p>{details.description}</p>
          </div>
        )}
        {!changes.length && !metadata.length && !details.description && (
          <p className={styles.noDetails}>
            No additional details were recorded for this action.
          </p>
        )}
      </div>
    </li>
  );
}

export const InventoryActivity = ({
  instrumentId,
  instrumentName,
  ticker,
  currency,
}) => {
  const [page, setPage] = useState(1);
  const [expandedIds, setExpandedIds] = useState([]);
  const scrollRef = useRef(null);
  const {data, loading, error, refetch} = useQuery(INVENTORY_ACTIVITY, {
    variables: {instrumentId, page, limit: PAGE_SIZE},
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });
  const result = data?.getInstrumentInventoryActivity;
  const events = error ? [] : result?.data || [];
  const total = result?.total || 0;
  const allExpanded =
    events.length > 0 && events.every(event => expandedIds.includes(event.id));
  // Group only the server's current page; pagination retains the full history.
  const groups = events.reduce((days, event) => {
    const date = event.createdAt
      ? moment.utc(event.createdAt)
      : moment.invalid();
    const label = date.isValid()
      ? date.format('DD MMM YYYY')
      : 'Date unavailable';
    const previous = days[days.length - 1];
    if (previous?.label === label) {
      previous.events.push(event);
    } else {
      days.push({label, events: [event]});
    }
    return days;
  }, []);
  const changePage = next => {
    setPage(next);
    setExpandedIds([]);
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  };

  return (
    <section className={styles.activity} aria-label="Inventory activity">
      <header className={styles.heading}>
        <h2>Inventory activity</h2>
        <p className={styles.instrument}>
          {ticker && <strong>{ticker}</strong>}
          {instrumentName || 'Admin changes to inventory'}
        </p>
        <div className={styles.toolbar}>
          <span className={styles.count}>
            {error
              ? 'Activity unavailable'
              : loading && !data
              ? 'Loading activity…'
              : `${total} ${total === 1 ? 'event' : 'events'}`}
            <span>Newest first · UTC</span>
          </span>
          <div className={styles.actions}>
            {events.length > 0 && (
              <Button
                type="text"
                disabled={loading}
                onClick={() =>
                  setExpandedIds(
                    allExpanded ? [] : events.map(event => event.id),
                  )
                }>
                {allExpanded ? 'Collapse all' : 'Expand all'}
              </Button>
            )}
            <Button
              icon={<ReloadOutlined />}
              onClick={() => refetch()}
              loading={loading}
              aria-label="Refresh inventory activity">
              Refresh
            </Button>
          </div>
        </div>
      </header>
      <div className={styles.history} ref={scrollRef} aria-busy={loading}>
        {error ? (
          <Alert
            type="error"
            showIcon
            message="Could not load inventory activity."
            description="Refresh to try again. Your recorded history has not changed."
            action={
              <Button size="small" onClick={() => refetch()}>
                Retry
              </Button>
            }
          />
        ) : loading ? (
          <div
            className={styles.loading}
            role="status"
            aria-label="Loading inventory activity">
            <Skeleton active paragraph={{rows: 3}} />
            <Skeleton active paragraph={{rows: 3}} />
          </div>
        ) : !events.length ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <>
                <strong>No inventory activity yet</strong>
                <p>
                  Admin top-ups, removals and settings changes will appear here.
                </p>
              </>
            }
          />
        ) : (
          groups.map(group => (
            <section
              className={styles.day}
              key={group.label}
              aria-label={group.label}>
              <h3 className={styles.dateHeading}>
                {group.label}
                <span>
                  {group.events.length}{' '}
                  {group.events.length === 1 ? 'event' : 'events'}
                </span>
              </h3>
              <ol className={styles.events}>
                {group.events.map(event => (
                  <ActivityEvent
                    key={event.id}
                    event={event}
                    currency={currency}
                    expanded={expandedIds.includes(event.id)}
                    onToggle={() =>
                      setExpandedIds(ids =>
                        ids.includes(event.id)
                          ? ids.filter(id => id !== event.id)
                          : [...ids, event.id],
                      )
                    }
                  />
                ))}
              </ol>
            </section>
          ))
        )}
      </div>
      <footer className={styles.footer}>
        <p>Earlier settings changes are not available.</p>
        {!error && total > PAGE_SIZE && (
          <Pagination
            size="small"
            current={page}
            pageSize={PAGE_SIZE}
            total={total}
            showSizeChanger={false}
            disabled={loading}
            onChange={changePage}
          />
        )}
      </footer>
    </section>
  );
};
